/**
 * GLSL — one frame of the animation, drawn on the GPU from the textures `emit/towertex.ts`
 * lays out. This is the third renderer of the same picture, and the rules are the same as for
 * the second.
 *
 * `emit/svg.ts` draws the still, the payload's player in `emit/html.ts` draws the loop, and
 * this draws the loop 4096 times at once so `apps/tower` can animate the whole collection.
 * `tools/playercheck.ts` proves the second is the first; `apps/tower/shadercheck.ts` proves
 * this is the second, cell by cell and pixel by pixel.
 *
 * **Every number is interpolated, never typed in.** A shader is a string, so a constant in it
 * is invisible to the compiler and to every test that does not run a GPU. The grid size, the
 * glyph indices, the empty cell, the flicker slots, the ghost slots and alpha, the schedule's
 * bit layout and the hash multipliers all arrive from the modules that define them, which is
 * why this file is TypeScript that emits GLSL rather than a `.glsl` file.
 *
 * **Byte space, not linear space.** The player composites in sRGB bytes: `fillStyle` is a hex
 * string and `globalAlpha` blends what is in the framebuffer. So the palette texture is raw
 * bytes (`RGBA8`, no `SRGBColorSpace` tag), `texelFetch` hands back `byte / 255`, every `mix`
 * here happens in that space, and nothing re-encodes on the way out — a `ShaderMaterial`
 * gets no output-encode chunk from three, which for once is exactly what we want.
 *
 * **`highp int` is load-bearing.** The schedule's largest texel index is 196,607 and a
 * `mediump int` is only guaranteed to hold 32,767, so a shader compiled at medium precision
 * would fetch the wrong frame for three quarters of the collection. `hash32` needs the full
 * 32-bit wrap for the same reason. Both are declared below rather than assumed from the
 * host: three's prefix happens to ask for `highp`, and this file does not depend on it.
 */

import { ELEVATION_LEVELS, GLITCH_FRAMES, GRID_COLS, GRID_ROWS } from "../constants.js";
import { EMPTY_CELL, GLYPH_BLANK, GLYPH_COUNT, GLYPH_FILL } from "../art/cell.js";
import {
  FLICKER_SLOT_A,
  FLICKER_SLOT_B,
  GHOST_ALPHA,
  GHOST_SLOT_LEFT,
  GHOST_SLOT_RIGHT,
} from "../art/glitch.js";
import { SLOT_BG } from "../art/palette.js";
import { HASH_MIX_A, HASH_MIX_B, HASH_SEED_MUL, HASH_X_MUL, HASH_Y_MUL } from "../engine/int.js";
import {
  BAND_MAG_MAX,
  BAND_MAG_SHIFT,
  BAND_SIGN_BIT,
  BLOCKS_PER_SIDE,
  BLOCK_CELLS,
  BLOCK_TOP_LEVEL,
  CELL_BLOCKS_PER_ROW,
  GLYPH_TILE_PX,
  SCHED_FLAG_FLICKER,
  SCHED_FLAG_HOT,
  SCHED_HEADER_WORDS,
  SCHED_TEXELS_PER_FRAME,
  SCHED_TEX_W,
} from "./towertex.js";

/** A GLSL `uint` literal, hex, so a reader can compare it to the TypeScript by eye. */
function hex32(x: number): string {
  return `0x${(x >>> 0).toString(16).padStart(8, "0")}u`;
}

/**
 * Every constant the shader reads, quoted from the module that defines it.
 *
 * Emitted as `const` declarations rather than `#define`s so the compiler type-checks them:
 * `TR_FLAG_HOT` is a `uint` because it is masked against one, and a `#define` would have made
 * that an error at the use site instead of here.
 */
export function towerGlslConstants(): string {
  return `const int TR_COLS = ${GRID_COLS};
const int TR_ROWS = ${GRID_ROWS};
const int TR_BLOCKS_PER_ROW = ${CELL_BLOCKS_PER_ROW};
const int TR_EMPTY_CELL = ${EMPTY_CELL};
const int TR_GLYPH_BLANK = ${GLYPH_BLANK};
const int TR_GLYPH_COUNT = ${GLYPH_COUNT};
const int TR_RAMP_LEN = ${ELEVATION_LEVELS};
const int TR_SLOT_BG = ${SLOT_BG};
const int TR_FLICKER_A = ${FLICKER_SLOT_A};
const int TR_FLICKER_B = ${FLICKER_SLOT_B};
const int TR_GHOST_LEFT = ${GHOST_SLOT_LEFT};
const int TR_GHOST_RIGHT = ${GHOST_SLOT_RIGHT};
const float TR_GHOST_ALPHA = ${GHOST_ALPHA.toFixed(6)};
const int TR_FRAMES = ${GLITCH_FRAMES};
const int TR_SCHED_W = ${SCHED_TEX_W};
const int TR_SCHED_TEXELS = ${SCHED_TEXELS_PER_FRAME};
const uint TR_FLAG_HOT = ${SCHED_FLAG_HOT}u;
const uint TR_FLAG_FLICKER = ${SCHED_FLAG_FLICKER}u;
const int TR_BAND_MAG_MAX = ${BAND_MAG_MAX};
const int TR_BLOCK_CELLS = ${BLOCK_CELLS};
const int TR_BLOCKS_SIDE = ${BLOCKS_PER_SIDE};
const int TR_BLOCK_TOP = ${BLOCK_TOP_LEVEL};
const float TR_GLYPH_PX = ${GLYPH_TILE_PX}.0;`;
}
/**
 * The five texture uniforms, with their precision spelled out.
 *
 * `highp` on each sampler is not decoration. An integer sampler at medium precision would
 * hand back a `mediump uint`, which is only guaranteed to carry 16 bits — enough to destroy
 * both `hash32`'s avalanche and a 32-bit corruption seed. Names are shared with
 * `apps/tower/src/parcels.ts` through `TOWER_TEXTURE_UNIFORMS` so a rename cannot half-land.
 */
export const TOWER_TEXTURE_UNIFORMS = ["uCells", "uPalette", "uSched", "uBlocks", "uGlyphs"] as const;

export function towerGlslUniforms(): string {
  return `precision highp int;
precision highp float;
uniform highp usampler2D uCells;
uniform highp sampler2D uPalette;
uniform highp usampler2D uSched;
uniform highp sampler2D uBlocks;
uniform highp sampler2DArray uGlyphs;`;
}

/**
 * The library: the engine's arithmetic, in GLSL, and nothing else.
 *
 * Precision, uniforms, constants and functions — but no `main`, no varyings and no
 * `gl_FragColor`, so the same text serves the tower's material and the two throwaway shaders
 * `apps/tower/shadercheck.ts` builds to render intermediate values into integer targets. That is
 * how the mirror gets *measured* rather than eyeballed. Every function is named for the
 * TypeScript it mirrors.
 */
export function towerGlslLib(): string {
  return [
    towerGlslUniforms(),
    towerGlslConstants(),
    GLSL_HASH,
    GLSL_CELLS,
    GLSL_SCHEDULE,
    GLSL_FRAME_CELL,
    GLSL_PALETTE,
    GLSL_COVER,
    GLSL_COMPOSITE,
    GLSL_BLOCKS,
    GLSL_PARCEL,
  ].join("\n\n");
}
/**
 * `hash32` from `engine/int.ts`, bit for bit.
 *
 * GLSL's `uint` wraps on overflow and `>>` on a `uint` is logical, which is exactly what
 * `Math.imul` plus `>>> 0` buys in JavaScript — so this is the same avalanche with none of the
 * defensive machinery. `x` and `y` are cell coordinates in `0..31`, never negative, so the
 * `uint(int)` conversions here never have to argue about two's complement.
 */
const GLSL_HASH = `uint trHash32(int x, int y, uint seed) {
  uint h = uint(x) * ${hex32(HASH_X_MUL)} + uint(y) * ${hex32(HASH_Y_MUL)}
    + seed * ${hex32(HASH_SEED_MUL)};
  h ^= h >> 15u;
  h *= ${hex32(HASH_MIX_A)};
  h ^= h >> 12u;
  h *= ${hex32(HASH_MIX_B)};
  h ^= h >> 15u;
  return h;
}`;

/**
 * The cell texture: `cellsOffset` as texel coordinates, and the two nibble accessors.
 *
 * `trCell` returns the empty cell off-grid, which is `get()` in `art/cell.ts` and the
 * `(sx<0||sx>=W)` test in the player. It is the whole reason a displacement has an edge: the
 * cells a band vacates read as background rather than wrapping around.
 */
const GLSL_CELLS = `ivec2 trCellOrigin(int id) {
  return ivec2((id % TR_BLOCKS_PER_ROW) * TR_COLS, (id / TR_BLOCKS_PER_ROW) * TR_ROWS);
}

int trCellRaw(int id, int x, int y) {
  return int(texelFetch(uCells, trCellOrigin(id) + ivec2(x, y), 0).r);
}

int trCell(int id, int x, int y) {
  if (x < 0 || x >= TR_COLS || y < 0 || y >= TR_ROWS) return TR_EMPTY_CELL;
  return trCellRaw(id, x, y);
}

int trCellGlyph(int cell) { return cell >> 4; }

int trCellSlot(int cell) { return cell & 15; }`;
/**
 * The schedule texture: one header fetch, then one band word.
 *
 * `bandOfRow` is inlined here — `((y + 1) * bands - 1) / TR_ROWS`, the exact inverse of
 * `bandRows`, proven against it for every band count and row by `validateTowerTex`. It earns
 * its place by turning "find the band containing row y" into arithmetic: scanning seven band
 * words would be a second texel fetch on every fragment of every animated parcel.
 *
 * The largest texel index this computes is 196,607, which is why `precision highp int` is
 * declared and not assumed.
 */
const GLSL_SCHEDULE = `struct TrFrame {
  int index;
  bool hot;
  bool flicker;
  int chroma;
  int bands;
  uint seed;
};

ivec2 trSchedTexel(int id, int frame, int texel) {
  int i = (id * TR_FRAMES + frame) * TR_SCHED_TEXELS + texel;
  return ivec2(i % TR_SCHED_W, i / TR_SCHED_W);
}

TrFrame trFrameHeader(int id, int frame) {
  uvec4 h = texelFetch(uSched, trSchedTexel(id, frame, 0), 0);
  TrFrame f;
  f.index = frame;
  f.hot = (h.x & TR_FLAG_HOT) != 0u;
  f.flicker = (h.x & TR_FLAG_FLICKER) != 0u;
  f.chroma = int(h.y);
  f.bands = int(h.z);
  f.seed = h.w;
  return f;
}

int trBandShift(int id, int frame, int bands, int y) {
  int at = ${SCHED_HEADER_WORDS} + ((y + 1) * bands - 1) / TR_ROWS;
  uvec4 v = texelFetch(uSched, trSchedTexel(id, frame, at / 4), 0);
  int word = int(v[at % 4]);
  int mag = (word >> ${BAND_MAG_SHIFT}) & TR_BAND_MAG_MAX;
  return (word & ${BAND_SIGN_BIT}) != 0 ? -mag : mag;
}`;
/**
 * `applyFrame`, for one cell of one frame — the gather, then the corruption.
 *
 * Three details are the whole parity story, and each of them is a one-line trap.
 *
 * The guard is on the *requested* cell and comes first. A ghost pass asks for cells outside the
 * grid (that is what an offset copy is), and the player, which paints cell `x` at `x + dx`,
 * simply painted nothing off the canvas. Gathering first and guarding second would let a
 * negative shift drag a real cell in from the far edge — visible only on hot frames of tokens
 * that also have chroma, which is a bug that ships.
 *
 * The corruption hash is addressed by the cell's *final* position, so the grain stays nailed to
 * the frame while ink slides through it, and it replaces the glyph while keeping the slot, so a
 * corrupted cell can never name a colour the palette did not author. Blanks are skipped, which
 * is what keeps a displacement's opened edge visible.
 */
const GLSL_FRAME_CELL = `int trFrameCell(int id, TrFrame f, int x, int y, int corrupt) {
  if (x < 0 || x >= TR_COLS || y < 0 || y >= TR_ROWS) return TR_EMPTY_CELL;
  if (!f.hot) return trCellRaw(id, x, y);
  int cell = trCell(id, x - trBandShift(id, f.index, f.bands, y), y);
  if (corrupt > 0 && trCellGlyph(cell) != TR_GLYPH_BLANK) {
    uint w = trHash32(x, y, f.seed);
    if (int(w % 256u) < corrupt) {
      cell = (int((w >> 8u) % uint(TR_RAMP_LEN)) << 4) | trCellSlot(cell);
    }
  }
  return cell;
}`;

/**
 * The palette, and the flicker swap.
 *
 * The player swaps two entries of its colour array on a flicker frame and then indexes it by
 * slot; this maps the slot instead and indexes the texture once. Same picture, no copy. The
 * fetch is a `texelFetch`, so no filtering and no sRGB decode can get between the baked byte
 * and the blend — see the file header on why that matters.
 */
const GLSL_PALETTE = `int trFlicker(int slot, bool on) {
  if (!on) return slot;
  if (slot == TR_FLICKER_A) return TR_FLICKER_B;
  if (slot == TR_FLICKER_B) return TR_FLICKER_A;
  return slot;
}

vec3 trSlotColor(int id, int slot, bool flicker) {
  return texelFetch(uPalette, ivec2(trFlicker(slot, flicker), id), 0).rgb;
}`;
/**
 * Ink coverage of a cell's glyph, from the array atlas.
 *
 * Branchless on purpose. The blank glyph's layer is baked all zero and the fill glyph's layer
 * all 255, so the two cases that would otherwise need an `if` are answered by the same fetch as
 * every other glyph — and the baker asserts those two layers rather than trusting them.
 *
 * `lod` comes from the caller because a fragment needs the same level for all three passes, and
 * because the far LOD wants to force it. `textureLod` on an array texture minifies inside one
 * layer, so no glyph can bleed into its neighbour however hard the parcel is squeezed.
 */
const GLSL_COVER = `float trCover(int glyphSet, int cell, vec2 uv, float lod) {
  float layer = float(glyphSet * TR_GLYPH_COUNT + trCellGlyph(cell));
  return textureLod(uGlyphs, vec3(uv, layer), lod).r;
}`;

/**
 * One frame of one token, composited exactly as `draw()` composites it in the payload.
 *
 * Background, then the two chroma ghosts at `GHOST_ALPHA`, then the frame itself — in that
 * order, because alpha compositing is not commutative. The ghosts read cells the player would
 * have *painted* here: it draws cell `x` at `x + dx`, so the cell landing on this fragment is
 * `x - dx`, and the left ghost's `dx` is `-chroma`. Both offsets are whole cells, which is why
 * one cell-local `uv` serves all three passes.
 *
 * `mix(under, over, alpha * cover)` is `globalAlpha` times the mark's antialiased coverage,
 * blended in sRGB byte space. Two known and measured differences from the canvas: the glyph
 * atlas clips whatever ink a font puts outside its cell box, and the GPU blends in float where
 * canvas rounds to bytes per pass. `apps/tower/shadercheck.ts` reports both as pixel error
 * against resvg instead of claiming they are zero.
 */
const GLSL_COMPOSITE = `vec3 trFrameColor(int id, int frame, int glyphSet, int corrupt, vec2 grid, float lod) {
  ivec2 c = ivec2(floor(grid));
  vec2 uv = grid - vec2(c);
  TrFrame f = trFrameHeader(id, frame);
  vec3 col = trSlotColor(id, TR_SLOT_BG, f.flicker);
  if (f.chroma > 0) {
    int left = trFrameCell(id, f, c.x + f.chroma, c.y, corrupt);
    col = mix(col, trSlotColor(id, TR_GHOST_LEFT, f.flicker),
      TR_GHOST_ALPHA * trCover(glyphSet, left, uv, lod));
    int right = trFrameCell(id, f, c.x - f.chroma, c.y, corrupt);
    col = mix(col, trSlotColor(id, TR_GHOST_RIGHT, f.flicker),
      TR_GHOST_ALPHA * trCover(glyphSet, right, uv, lod));
  }
  int cell = trFrameCell(id, f, c.x, c.y, corrupt);
  return mix(col, trSlotColor(id, trCellSlot(cell), f.flicker), trCover(glyphSet, cell, uv, lod));
}`;
/**
 * The far LOD: one fetch from the block texture, at an explicit mip level.
 *
 * `texelFetch` rather than `texture`, because a filtered fetch near a tile's edge would blend a
 * neighbouring *token* into this one. A token's tile is 8x8 texels and levels 1..3 are its 8-,
 * 16- and 32-cell means, all of them still inside the tile — which is exactly why
 * `BLOCK_TOP_LEVEL` is 3 and why the shader may not ask for more.
 */
const GLSL_BLOCKS = `vec3 trBlock(int id, vec2 grid, int level) {
  ivec2 tile = ivec2(id % TR_BLOCKS_PER_ROW, id / TR_BLOCKS_PER_ROW) * TR_BLOCKS_SIDE;
  ivec2 b = clamp(ivec2(grid) / TR_BLOCK_CELLS, ivec2(0), ivec2(TR_BLOCKS_SIDE - 1));
  return texelFetch(uBlocks, (tile >> level) + (b >> level), level).rgb;
}`;

/**
 * The ladder: draw the frame up close, the block means far away, cross-fade in between.
 *
 * The crossovers are Nyquist limits, not taste. `cpp` is how many cells one pixel covers, from
 * the derivatives of the *continuous* grid coordinate — of `grid` and never of `fract(grid)`,
 * which would spike at every cell boundary and put a seam on every cell. Point-sampling the
 * cell grid is honest while `cpp <= 1`; the block grid is 4 cells wide, so it is honest while
 * `cpp <= 4`. Between those two numbers the two agree closely enough to fade, which is the
 * whole trick: a parcel 8 pixels tall costs one texel fetch instead of 1024 cells of gather,
 * and that is what makes 4096 animated parcels affordable at all.
 *
 * `detail` moves both crossovers together — the tower's HUD slider — so the cost of detail can
 * be measured rather than argued about. The glyph level is the same reasoning one texture down:
 * a cell is `TR_GLYPH_PX` glyph texels across, so `log2(cpp * TR_GLYPH_PX)` is the mip whose
 * texel matches the pixel, and the hardware prefilters the mark's coverage from there.
 */
const GLSL_PARCEL = `float trCellsPerPixel(vec2 grid) {
  return max(length(dFdx(grid)), length(dFdy(grid)));
}

vec3 trParcel(int id, int frame, int glyphSet, int corrupt, vec2 grid, float detail) {
  float cpp = trCellsPerPixel(grid);
  float scaled = cpp / max(detail, 0.0625);
  float fade = smoothstep(1.0, float(TR_BLOCK_CELLS), scaled);
  int level = int(clamp(round(log2(max(scaled, 1.0) / float(TR_BLOCK_CELLS))), 0.0, float(TR_BLOCK_TOP)));
  if (fade >= 1.0) return trBlock(id, grid, level);
  float lod = max(0.0, log2(max(cpp, 1e-6) * TR_GLYPH_PX));
  vec3 near = trFrameColor(id, frame, glyphSet, corrupt, grid, lod);
  if (fade <= 0.0) return near;
  return mix(near, trBlock(id, grid, level), fade);
}`;
/**
 * The instanced attributes and the two per-draw uniforms the parcel material needs.
 *
 * One `InstancedMesh` draws the whole tower, so everything that varies per parcel is an
 * attribute: its id (which addresses all four data textures), its glyph set and corruption
 * chance (traits the textures do not carry), a dim factor the tower uses for depth cueing, and
 * whether it is drawn at all. `uFrame` is shared — the loop is one clock for the whole
 * collection, and every token's schedule already makes its own frames hot.
 *
 * `aShow` is a *presence* flag and not a policy: which parcels are shown is the tower's
 * business (floors revealed, one floor isolated, a trait filter), and all this file promises is
 * that a zero costs nothing. It has to be here rather than in the app because the only way to
 * make a parcel free is to collapse it before rasterisation — see the vertex shader — and
 * `aDim = 0` would not do it: a black quad still shades every fragment it covers, and still
 * occludes whatever is behind it.
 */
export const TOWER_INSTANCE_ATTRIBUTES = ["aId", "aGlyphSet", "aCorrupt", "aDim", "aShow"] as const;
export const TOWER_FRAME_UNIFORMS = ["uFrame", "uDetail"] as const;

/**
 * The parcel material's vertex shader.
 *
 * `vGrid` is the fragment's position in cell space, y flipped because a plane's `uv` starts at
 * the bottom and row 0 of the grid is the top. It is interpolated as a plain `vec2`, so the
 * fragment shader's derivatives of it are the parcel's real on-screen scale — which is what the
 * LOD ladder reads. The traits go across `flat`: an interpolated integer would be a different
 * token in the middle of a triangle.
 *
 * A hidden parcel is sent to `vec4(2, 2, 2, 1)`, which is outside the clip volume in x and y, so
 * the whole primitive is discarded before it costs a single fragment of gather. That matters at
 * this scale: the tower's fragment stage is 1024 cells of animated art per parcel, and a stack
 * with two of sixteen floors revealed must cost two floors and not sixteen.
 */
export function parcelVertexGlsl(): string {
  return `${towerGlslConstants()}

in float aId;
in float aGlyphSet;
in float aCorrupt;
in float aDim;
in float aShow;

flat out int vId;
flat out int vGlyphSet;
flat out int vCorrupt;
out float vDim;
out vec2 vGrid;

void main() {
  vId = int(aId + 0.5);
  vGlyphSet = int(aGlyphSet + 0.5);
  vCorrupt = int(aCorrupt + 0.5);
  vDim = aDim;
  vGrid = vec2(uv.x, 1.0 - uv.y) * vec2(float(TR_COLS), float(TR_ROWS));
#ifdef USE_INSTANCING
  vec4 local = instanceMatrix * vec4(position, 1.0);
#else
  vec4 local = vec4(position, 1.0);
#endif
  gl_Position = aShow > 0.5
    ? projectionMatrix * modelViewMatrix * local
    : vec4(2.0, 2.0, 2.0, 1.0);
}`;
}
/**
 * The parcel material's fragment shader.
 *
 * Nothing re-encodes on the way out. The composite is sRGB bytes, the canvas presents sRGB, and
 * three adds no output-encode chunk to a `ShaderMaterial` — so writing `col` straight into
 * `gl_FragColor` is what makes the parcel match the SVG. The stills path has the opposite bug
 * today, sampling an `SRGB8_ALPHA8` atlas (which the hardware decodes to linear) and writing
 * those values as if they were sRGB, which is why it renders dark.
 *
 * `gl_FragColor` is deliberate and it constrains the host. This text is ES 3.00 — `texelFetch`,
 * `usampler2D`, `flat` — and a `ShaderMaterial` gets `#version 300 es` from three either way, but
 * the `out` declaration behind `gl_FragColor` only appears in three's prefix when `glslVersion` is
 * *not* set to `GLSL3` (`WebGLProgram.js` emits `layout(location = 0) out highp vec4 pc_fragColor`
 * and the matching `#define` on that branch alone). So the material must leave `glslVersion`
 * alone, and `apps/tower/src/parcels.ts` says so where it builds one.
 */
export function parcelFragmentGlsl(): string {
  return `${towerGlslLib()}

uniform int uFrame;
uniform float uDetail;

flat in int vId;
flat in int vGlyphSet;
flat in int vCorrupt;
in float vDim;
in vec2 vGrid;

void main() {
  vec3 col = trParcel(vId, uFrame, vGlyphSet, vCorrupt, vGrid, uDetail);
  gl_FragColor = vec4(col * vDim, 1.0);
}`;
}
